import React, {Children} from 'react';
import {CSSTransition, TransitionGroup} from 'react-transition-group';

import {isTestEnvironment} from '@pb/kit-util-is-test-environment';

import type {ComponentProps, FC, ReactElement, ReactNode} from 'react';


/**
 * Timeout type
 */
type Timeout<T> = {
    /**
     * Animation Enter Timeout
     */
    enter?: T,

    /**
     * Animation Leave Timeout
     */
    exit?: T,
};

/**
 * Children Props
 */
type ChildrenProps = {
    /**
     * Children Node
     */
    children: ReactNode | ReactNode[],
};
type ClassNamesType =
    | {
        appear?: string,
        appearActive?: string,
        appearDone?: string,
        enter?: string,
        enterActive?: string,
        enterDone?: string,
        exit?: string,
        exitActive?: string,
        exitDone?: string,
    }
    | string;

/**
 * Animation Props
 */
export interface Props extends Omit<ComponentProps<typeof CSSTransition>, 'timeout' | 'nodeRef'> {
    /**
     * Animation name
     */
    classNames: ClassNamesType,

    /**
     * Animation Timeout
     */
    timeout?: number | Timeout<number>,

    /**
     * Root animation component
     */
    component?: ComponentProps<typeof TransitionGroup>['component'],

    /**
     * Children Node
     */
    children: ReactNode | ReactNode[],

    /**
     * Animation Enter Timeout
     */
    enter?: boolean,

    /**
     * Animation Leave Timeout
     */
    exit?: boolean,
    // Not sure what it is, added just because of it exists in defaultProps

    /**
     * A convenience prop that enables or disables appear animations
     * for all children. Note that specifying this will override any defaults set
     * on individual children Transitions.
     */
    appear?: boolean,
    // CSSTransition Props

    /**
     * A <Transition> callback fired immediately after the 'enter' or 'appear' classes are removed and the done class is
     * added to the DOM node.
     */
    onEntered?: () => void,

    /**
     * A <Transition> callback fired immediately after the 'exit' classes are removed and the exit-done class is added
     * to the DOM node
     */
    onExited?: () => void,

    /**
     * A ref object that points to the transitioning DOM node.
     */
    nodeRef: React.RefObject<HTMLElement | null>,
}

const rootChild = ({children}: ChildrenProps) =>
    Children.toArray(children)[0] ?? null;

/**
 * The Animation component
 */
export const Animation: FC<Props> = function Animation(props: Props) {
    const {
        appear = false,
        enter = true,
        exit = true,
        timeout = 300,
        component = rootChild,
        children,
        nodeRef,
        ...rest
    } = props;

    if (isTestEnvironment()) {
        return <span>{children}</span>;
    } else {
        return (
            <TransitionGroup component={component}>
                {Children.map(children, (child, i) => {
                    return (
                        child && (
                            <CSSTransition
                                key={(child as ReactElement).key ?? i}
                                appear={appear}
                                enter={enter}
                                exit={exit}
                                timeout={timeout}
                                nodeRef={nodeRef}
                                {...rest}>
                                {child}
                            </CSSTransition>
                        )
                    );
                })}
            </TransitionGroup>
        );
    }
};
